import { useState } from 'react';
import { Alert, Button, Card, Input, Spin, Tag, Typography } from 'antd';
import { useMutation } from '@tanstack/react-query';
import type { FormInstance } from 'antd/lib/form';
import type { PartnerRule, PartnerRuleCriterion } from '@shipium-corp/shipium-ts-types';
import { mastraService } from '~/services';
import type {
  AvailableCarrier,
  AvailableOrigin,
  AvailableTenant,
  DraftRuleCriterion,
  DraftRuleResponse,
} from '~/services/mastra-service';
import { initializePopulatedForm } from './utils';
import type { RulePageForm } from './types';

const { Paragraph, Text } = Typography;

interface RuleDrafterProps {
  form: FormInstance<Partial<RulePageForm>>;
  partnerId: string;
  availableCarriers?: AvailableCarrier[];
  availableTenants?: AvailableTenant[];
  availableOrigins?: AvailableOrigin[];
  /** Called with the synthetic rule after the form is populated, so the page
   *  can sync UI state that lives outside the form (e.g. toggles that control
   *  conditional sections). */
  onApplied?: (rule: PartnerRule) => void;
}

function flatToNestedCriterion(c: DraftRuleCriterion): PartnerRuleCriterion {
  return {
    ruleProperty: { rulePropertyEnum: c.rulePropertyEnum as PartnerRuleCriterion['ruleProperty']['rulePropertyEnum'] },
    ruleComparator: {
      ruleComparatorEnum: c.ruleComparatorEnum as PartnerRuleCriterion['ruleComparator']['ruleComparatorEnum'],
    },
    compareValue: c.compareValue as object,
    ...(c.scalarUnitEnum
      ? {
          scalarUnit: {
            scalarUnitEnum: c.scalarUnitEnum as NonNullable<PartnerRuleCriterion['scalarUnit']>['scalarUnitEnum'],
          },
        }
      : {}),
  };
}

function draftToSyntheticRule(draft: DraftRuleResponse['draft'], partnerId: string): PartnerRule {
  return {
    partnerId,
    name: draft.name,
    description: draft.description ?? '',
    criteria: draft.criteria.map(flatToNestedCriterion),
    criteriaMatchStrategy: draft.criteriaMatchStrategy,
    carrierAndServiceMethodList: draft.carrierAndServiceMethodList,
    tenantIds: draft.tenantIds ?? [],
    listType: draft.listType,
    terminating: draft.terminating,
  };
}

export const RuleDrafter = ({
  form,
  partnerId,
  availableCarriers,
  availableTenants,
  availableOrigins,
  onApplied,
}: RuleDrafterProps) => {
  const [text, setText] = useState('');
  const [lastResult, setLastResult] = useState<DraftRuleResponse | null>(null);

  const draftMutation = useMutation({
    mutationFn: (input: string) =>
      mastraService.draftRuleFromText(input, availableCarriers, availableTenants, availableOrigins),
    onSuccess: (response) => {
      setLastResult(response);
      const syntheticRule = draftToSyntheticRule(response.draft, partnerId);
      initializePopulatedForm(form, syntheticRule);
      // Conditionally rendered Form.Items (postal codes, ship options, etc.) aren't
      // mounted at the moment we set values — Form.useWatch reads from mounted fields,
      // so chips don't render. Re-apply on the next tick once the conditionals have
      // mounted from the toggles we just flipped.
      requestAnimationFrame(() => {
        form.setFieldsValue(form.getFieldsValue());
        form.setFieldValue('__touched', true);
      });
      onApplied?.(syntheticRule);
    },
  });

  const handleGenerate = () => {
    if (!text.trim()) return;
    draftMutation.mutate(text.trim());
  };

  const isLoading = draftMutation.isPending;
  const errorMessage = draftMutation.error
    ? "We couldn't draft a rule from that description. Please try rephrasing or fill out the form manually."
    : null;

  return (
    <Card
      title={
        <>
          ✨ Describe your rule <Tag color="default">Alpha</Tag>
        </>
      }
      style={{ width: '100%' }}
    >
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Describe the rule you want in plain English (e.g. "Block UPS Ground for shipments over 50 pounds going to
        Alaska"). We'll pre-fill the form below — review and edit before saving.
      </Paragraph>

      <Input.TextArea
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Block UPS Ground for shipments over 50 pounds going to Alaska"
        disabled={isLoading}
      />

      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" onClick={handleGenerate} loading={isLoading} disabled={!text.trim()}>
          ✨ Generate
        </Button>
      </div>

      {isLoading && (
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <Spin />
          <Paragraph style={{ marginTop: 8, marginBottom: 0 }} type="secondary">
            Drafting rule...
          </Paragraph>
        </div>
      )}

      {errorMessage && <Alert style={{ marginTop: 12 }} type="error" showIcon message={errorMessage} />}

      {lastResult && !isLoading && (
        <div style={{ marginTop: 16 }}>
          <Alert
            type={lastResult.validationPassed ? 'info' : 'warning'}
            showIcon
            message={
              lastResult.validationPassed
                ? 'Draft applied to the form below'
                : 'Draft applied — please review the issues below'
            }
            description={
              <>
                <Paragraph style={{ marginBottom: 8 }}>
                  <Text strong>Interpretation: </Text>
                  {lastResult.rationale}
                </Paragraph>
                {lastResult.clarifications.length > 0 && (
                  <>
                    <Text strong>Things to confirm:</Text>
                    <ul style={{ marginTop: 4, marginBottom: 0, paddingLeft: 20 }}>
                      {lastResult.clarifications.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            }
          />
        </div>
      )}
    </Card>
  );
};
